<template>
  <div class="d-flex">
    <div class="pr-8 sidebar border-r">
      <div class="sidebar-content" :class="{ 'has-asset-visit': assetVisit?.id }">
        <h3 class="text-black">{{ $t('global.basicTerms.summary') }}</h3>
        <p v-if="shouldShowSummaryDescription" class="font-size-x-small py-2 text-dense">
          {{ $t('appointment.forms.createAppointmentForm.descriptions.summary') }}.
        </p>
        <div class="py-3">
          <template v-if="shouldShowStep1Summary">
            <summary-item
              class="mb-4"
              :title="$t('global.specialtyTerms.carrierContacts')"
              :data="selectedCarrierSummary"></summary-item>
            <summary-item
              class="mb-4"
              :title="$t('global.specialtyTerms.warehouses')"
              :data="selectedWarehouse.name"></summary-item>
            <summary-item
              class="mb-4"
              :title="$t('global.specialtyTerms.loadTypes')"
              :data="selectedLoadType.name"></summary-item>
            <summary-item
              class="mb-4"
              :title="$t('global.specialtyTerms.docks', selectedDocks.length)"
              :data="selectedDocksSummary"></summary-item>
          </template>
          <summary-item
            v-if="shouldShowStep2Summary"
            class="mb-4"
            :title="handlingUnitsLabel"
            :data="units ? String(units) : 'N/A'"></summary-item>
          <summary-item
            v-if="shouldShowDateTimeSummary"
            class="mb-4"
            :title="$t('appointment.forms.createAppointmentForm.tabLabels.dateTime')"
            :data="dateTimeSummary"></summary-item>
        </div>

        <template v-if="assetVisit?.id">
          <v-divider class="my-4"></v-divider>
          <div>
            <h3 class="text-black">
              {{ $t('appointment.forms.createAppointmentForm.tabLabels.assetDetails') }}
            </h3>
            <p class="font-size-x-small pt-2 pb-0 text-dense">
              {{ $t('appointment.forms.createAppointmentForm.descriptions.assetDetails') }}
            </p>
            <div
              class="d-flex flex-column"
              data-testid="create-appointment-dialog-asset-details-container">
              <asset-visit-form-data
                variant="list"
                size="small"
                :phone-title="$t('global.basicTerms.phone')"
                :company-title="$t('global.specialtyTerms.companies')"
                :asset-visit="assetVisit"
                :warehouse="selectedWarehouse" />
            </div>
          </div>
        </template>
      </div>
    </div>
    <form-base
      :header="header"
      v-if="!submitted"
      class="flex-grow-1 pl-8"
      :teleport-target="dialogTeleportTarget">
      <template v-slot:form>
        <v-form ref="form" class="create-appointment-form inline-fields">
          <v-stepper v-model="step" class="full-width">
            <v-stepper-header>
              <v-stepper-item :complete="step > 1" :value="1" color="primary">
                {{ $t('appointment.forms.createAppointmentForm.tabLabels.basicInformation') }}
              </v-stepper-item>

              <v-divider></v-divider>

              <template v-if="shouldShowHandlingUnits">
                <v-stepper-item
                  :complete="step > 2"
                  :value="2"
                  color="primary"
                  class="step-label-truncate">
                  <span>
                    {{ handlingUnitsLabel }}
                  </span>
                </v-stepper-item>

                <v-divider></v-divider>
              </template>

              <v-stepper-item :complete="step > dateTimeStep" :value="dateTimeStep" color="primary">
                {{ $t('appointment.forms.createAppointmentForm.tabLabels.dateTime') }}
              </v-stepper-item>

              <v-divider></v-divider>

              <v-stepper-item :value="appointmentDetailsStep" color="primary">
                {{ $t('appointment.forms.createAppointmentForm.tabLabels.appointmentDetails') }}
              </v-stepper-item>
            </v-stepper-header>

            <v-stepper-window class="mx-0 mt-2">
              <v-stepper-window-item :value="1" class="px-0 mt-11">
                <div class="d-flex flex-column gap-4">
                  <div>
                    <warehouse-select
                      data-testid="create-appointment-form-warehouse-select-input"
                      :disabled="!selectedCarrier && !context.hasOwnProperty('selectedWarehouse')"
                      :restricted-warehouse-ids="$me.warehouseAccessList"
                      :required="true"
                      :label="$t('global.specialtyTerms.warehouses')"
                      :tooltipText="
                        $t('appointment.forms.createAppointmentForm.tooltips.warehouseSelect')
                      "
                      v-model="selectedWarehouse"
                      :hide-icon="true"
                      @clear-warehouse="clearWarehouseContext"
                      @close="$emit('close')"
                      class="mb-4"></warehouse-select>
                  </div>
                  <div>
                    <carrier-select
                      required
                      density="compact"
                      v-model="selectedCarrier"
                      :warehouse="selectedWarehouse"
                      get-all-favorites
                      showWarning
                      v-if="isContextSet"
                      test-id="create-appointment-form-carrier-select-input"
                      entry-point="Create Appointment Modal"
                      :label="$t('global.specialtyTerms.carrierContacts')"
                      :tooltipText="
                        $t('appointment.forms.createAppointmentForm.tooltips.carrierContactSelect')
                      "
                      hide-icon></carrier-select>
                  </div>
                  <div>
                    <load-type-select
                      test-id="create-appointment-form-load-type-select-input"
                      :disabled="!selectedWarehouse.id"
                      :selected-docks="selectedDocks"
                      :required="true"
                      :label="$t('global.specialtyTerms.loadTypes')"
                      :tooltipText="
                        $t('appointment.forms.createAppointmentForm.tooltips.loadTypeSelect')
                      "
                      v-model="selectedLoadType"
                      :selected-warehouse="selectedWarehouse"
                      :showOnlyAssignedLoadTypes="true"
                      :hide-icon="true"
                      @close="$emit('close')"></load-type-select>
                  </div>
                  <div>
                    <dock-select
                      test-id="create-appointment-form-dock-select-input"
                      :disabled="!selectedWarehouse.id && !context.hasOwnProperty('selectedDocks')"
                      :label="$t('global.specialtyTerms.docks', 2)"
                      :tooltipText="
                        $t('appointment.forms.createAppointmentForm.tooltips.dockSelect')
                      "
                      :required="true"
                      :multi-select="true"
                      v-model="selectedDocks"
                      :docks="docksFilteredBySelectedLoadType"
                      :hide-icon="true"></dock-select>
                  </div>
                </div>
              </v-stepper-window-item>

              <v-stepper-window-item v-if="shouldShowHandlingUnits" :value="2" class="">
                <v-container>
                  <v-row class="my-4">
                    <h3 class="text-black typography-body-lg-semi-bold mb-1">
                      {{ handlingUnitsLabel }}
                      <span v-if="isHandlingUnitsRequired" class="text-error">*</span>
                      <help-icon-tooltip v-if="handlingUnitsHelperText">
                        {{ handlingUnitsHelperText }}
                      </help-icon-tooltip>
                    </h3>
                    <text-field
                      variant="outlined"
                      type="number"
                      v-model.number="units"
                      :placeholder="
                        $t('appointment.dialogs.appointmentDetailsDialog.placeholders.enterANumber')
                      "
                      hide-details="auto"
                      :rules="handlingUnitsRules"
                      :min="handlingUnitsMinAttr"
                      :max="handlingUnitsMaxAttr"
                      :required="isHandlingUnitsRequired"
                      :loading="fetchingUnavailableDates"
                      validate-on="input blur"
                      data-testid="create-appointment-form-handling-units-input"></text-field>
                  </v-row>
                  <v-row v-if="fetchingUnavailableDates" class="mt-2">
                    <v-col>
                      <v-progress-linear indeterminate color="primary" />
                    </v-col>
                  </v-row>
                </v-container>
              </v-stepper-window-item>

              <v-stepper-window-item :value="dateTimeStep" class="px-0">
                <date-time-picker
                  v-if="step === dateTimeStep"
                  v-model="selectedTime"
                  :selected-docks="selectedDocks"
                  :selected-load-type="selectedLoadType"
                  :start-date="selectedDate"
                  :selected-warehouse="selectedWarehouse"
                  :timezone="selectedWarehouse.timezone"
                  :unavailable-dates="unavailableDates"
                  :handling-units-label="handlingUnitsLabel"
                  @availability-error="handleAvailabilityError"
                  @date-range-changed="handleDateRangeChanged"></date-time-picker>
              </v-stepper-window-item>

              <v-stepper-window-item :value="appointmentDetailsStep" class="px-0">
                <v-container>
                  <v-row v-if="$refNumSettings(selectedWarehouse).isVisible">
                    <h3>
                      {{ referenceNumberLabel }}
                      <span v-if="$refNumSettings(selectedWarehouse).isRequired" class="text-error">
                        *
                      </span>
                      :
                      <help-icon-tooltip v-if="$refNumSettings(selectedWarehouse).helperText">
                        {{ $refNumSettings(selectedWarehouse).helperText }}
                      </help-icon-tooltip>
                      <help-icon-tooltip v-else>
                        Reference or PO Number associated with appointment or load delivery
                      </help-icon-tooltip>
                    </h3>
                    <feature-flag name="enable-multi-ref-input-ui">
                      <template #enabled>
                        <multi-ref-number-manager
                          outlined
                          outlined-chips
                          v-model="refNumbers"
                          data-testid="create-appointment-form-multi-ref-manager-input"
                          :should-enable-custom-tags="true"
                          small-chips
                          :rules="multiRefNumValidationRules"
                          :item-rules="refNumValidationRules"
                          :max-items="refNumSettings?.referenceNumbersMaximumCount"
                          required
                          :placeholder="refNumberPlaceholder"></multi-ref-number-manager>
                      </template>
                      <template #disabled>
                        <auto-expandable-textarea
                          v-model="refNumber"
                          data-testid="create-appointment-form-ref-number-input"
                          variant="underlined"
                          color="primary"
                          :label="refNumberPlaceholder"
                          :placeholder="refNumberPlaceholder"
                          validate-on-blur
                          :rules="refNumValidationRulesOld"
                          required
                          v-if="
                            $refNumSettings(selectedWarehouse).isRequired
                          "></auto-expandable-textarea>
                        <auto-expandable-textarea
                          v-else
                          v-model="refNumber"
                          :label="refNumberPlaceholder"
                          :placeholder="refNumberPlaceholder"
                          data-testid="create-appointment-form-ref-number-input"
                          :rules="refNumValidationRulesOld"
                          validate-on-blur></auto-expandable-textarea>
                      </template>
                    </feature-flag>
                  </v-row>

                  <custom-fields-old
                    v-if="selectedWarehouse.id"
                    :initial-custom-fields="customFields"
                    v-model="customFields"
                    :warehouse="selectedWarehouse"
                    @toggle-confirm-button="toggleConfirmButton"></custom-fields-old>

                  <create-appointment-custom-forms
                    v-if="selectedWarehouse.id && loadTypeTriggers.length"
                    :triggers="loadTypeTriggers"
                    @update:value="data => (customFormsData = data)"
                    :warehouse="selectedWarehouse"></create-appointment-custom-forms>

                  <v-row class="mt-10">
                    <h3>
                      {{ $t('appointment.forms.createAppointmentForm.labels.appointmentNotes') }}:
                      <help-icon-tooltip>
                        {{
                          $t(
                            'appointment.forms.createAppointmentForm.tooltips.includeAdditionalNotesForAppointment'
                          )
                        }}
                      </help-icon-tooltip>
                    </h3>

                    <rich-text-input
                      class="flex-fill"
                      :editor-config="{
                        plugins: ['Essentials', 'Paragraph'],
                        toolbar: { items: [] }
                      }"
                      v-model="notes"></rich-text-input>
                  </v-row>
                  <v-row class="mt-4">
                    <h3>
                      {{ $t('appointment.forms.createAppointmentForm.labels.appointmentTags') }}:
                      <help-icon-tooltip>
                        {{
                          $t(
                            'appointment.forms.createAppointmentForm.tooltips.selectFromListOfTagsOrCreateCustomTag'
                          )
                        }}
                      </help-icon-tooltip>
                    </h3>
                    <v-col>
                      <tag-manager
                        outlined
                        outlined-chips
                        v-model="tags"
                        :should-enable-custom-tags="true"
                        small-chips
                        placeholder="Add tags"></tag-manager>
                    </v-col>
                  </v-row>
                  <v-row class="mt-4">
                    <h3>
                      {{ $t('appointment.forms.createAppointmentForm.labels.emailSubscribers') }}:
                      <help-icon-tooltip>
                        listEmailsThatWillReceiveNotifications
                        {{
                          $t(
                            'appointment.forms.createAppointmentForm.tooltips.listEmailsThatWillReceiveNotifications'
                          )
                        }}
                      </help-icon-tooltip>
                    </h3>
                    <email-list-field
                      small-chips
                      small
                      v-model="ccEmails"
                      :disabled="fetchingOrgCarrierSettings"
                      ref="emailFieldList"></email-list-field>
                  </v-row>
                  <v-row class="mt-8">
                    <v-col>
                      <v-switch
                        density="compact"
                        :disabled="!areWarehouseNotificationsEnabled"
                        v-model="notificationsEnabled">
                        <template v-slot:label>
                          <span>
                            <v-icon size="small" class="ml-2 mr-1">
                              {{ notificationsEnabled ? 'mdi-bell' : 'mdi-bell-off-outline' }}
                            </v-icon>
                            <i18n-t
                              keypath="appointment.forms.createAppointmentForm.messages.emailNotificationsStatus"
                              tag="span">
                              <template #status>
                                <strong>
                                  {{ notificationsEnabled ? 'on' : 'off' }}
                                </strong>
                              </template>
                            </i18n-t>
                            <small v-if="!areWarehouseNotificationsEnabled">
                              ({{
                                $t(
                                  'appointment.forms.createAppointmentForm.messages.notificationsWillNotBeSentTheyAreNotEnabledInTheWarehouseSettings'
                                )
                              }})
                            </small>
                          </span>
                        </template>
                      </v-switch>
                    </v-col>
                  </v-row>
                </v-container>
              </v-stepper-window-item>
            </v-stepper-window>
          </v-stepper>
        </v-form>
      </template>
      <template #form-actions>
        <action-group
          :test-id="`create-appointment-form-step-${step}`"
          :loading="loading"
          :hide-cancel="true"
          :confirm-label="stepperLabelNext"
          :disable-confirm="disableConfirm || !showNextButton"
          @confirm="proceed">
          <template v-slot:additional-actions>
            <outline-button @click="goToPreviousStep" v-if="step > 1" before-icon="arrow-left">
              {{ $t('global.buttons.previous') }}
            </outline-button>
          </template>
        </action-group>
      </template>
    </form-base>
  </div>
</template>

<script>
import { isAxiosError } from '@satellite/plugins/util';
import { compact, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { scrollFirstErrorIntoView } from '@satellite/plugins/util';
import { computed, inject, ref, watch } from 'vue';
import { isDefined } from 'class-validator';
import customFormsService from '@satellite/services/custom-forms-service.js';
import notificationsHelper from '@satellite/helpers/notifications';
import { useI18n } from 'vue-i18n';
import { useAppointmentHelpers, useNotify, useValidator } from '@/composables';
import { getTranslatedRefNumberDisplayName } from '@nova/core';
import dockService from '@satellite/services/dock-service.js';
import { useStore } from 'vuex';
import { useFieldRules } from '@satellite/composables/useFieldRules.js';
import { FeatureFlag, useFlag } from '@loadsmart/vue-feature-flags';
import useLimiter from '@satellite/composables/useLimiter';

/**
 * Form that creates an appointment
 * @displayName Create Appointment Form
 */
export default {
  components: { FeatureFlag },
  props: {
    /**
     * Form header
     */
    header: {
      type: String,
      required: false
    },
    /**
     * Calendar context when dayview is clicked
     * contains date, time, warehouse, and dock
     */
    context: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    assetVisit: {
      type: Object,
      required: false
    },
    /*
     * If true, the form will follow to the appointment details page after creating an appointment
     */
    followToAppointmentDetails: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    referenceNumberLabel() {
      return getTranslatedRefNumberDisplayName(
        this.$refNumSettings(this.selectedWarehouse).displayName
      );
    },
    /**
     * Dynamic step numbers based on whether handling units is shown
     */
    dateTimeStep() {
      return this.shouldShowHandlingUnits ? 3 : 2;
    },
    appointmentDetailsStep() {
      return this.shouldShowHandlingUnits ? 4 : 3;
    },
    /**
     * Label for the next button depending on step
     * @returns {string}
     */
    stepperLabelNext() {
      return this.step < this.stepsTotal
        ? this.t('global.buttons.next')
        : this.t('global.buttons.create');
    },
    /**
     * If a warehouse has been selected
     * @returns {boolean}
     */
    warehouseSelected() {
      return !!(this.selectedWarehouse && this.selectedWarehouse.id);
    },
    /**
     * Display the next button in the stepper
     * @returns {*}
     */
    showNextButton() {
      let showNextButton = false;
      if (this.step === 1) {
        showNextButton = this.validateStep1();
      } else if (this.step === 2 && this.shouldShowHandlingUnits) {
        // Don't allow proceeding while fetching unavailable dates
        showNextButton = this.validateStep2() && !this.fetchingUnavailableDates;
      } else if (this.step === this.dateTimeStep) {
        showNextButton = this.validateDateTimeStep();
      } else {
        showNextButton = true;
      }

      return showNextButton;
    },
    shouldShowStep1Summary() {
      return Boolean(
        this.selectedWarehouse?.id &&
          this.selectedCarrier?.id &&
          this.selectedLoadType?.id &&
          this.selectedDocks.length
      );
    },
    shouldShowStep2Summary() {
      return this.shouldShowHandlingUnits && isDefined(this.units);
    },
    shouldShowDateTimeSummary() {
      return Boolean(this.selectedTime?.start);
    },
    shouldShowSummaryDescription() {
      return (
        !this.shouldShowStep1Summary &&
        !this.shouldShowStep2Summary &&
        !this.shouldShowDateTimeSummary
      );
    },
    selectedCarrierSummary() {
      return this.selectedCarrier?.id
        ? `${this.selectedCarrier.firstName} ${this.selectedCarrier.lastName} - ${this.selectedCarrier.company.name}`
        : '';
    },
    selectedDocksSummary() {
      return this.novaCore
        .deepClone(this.selectedDocks)
        .map(dock => dock.name)
        .join(', ');
    },
    dateTimeSummary() {
      return this.selectedTime?.start
        ? `${this.novaCore.formatDateTimeWithMilitarySupport(
            this.selectedTime.start.toISO(),
            this.selectedTime.start.zoneName,
            this.novaCore.LuxonDateTimeFormats.LongDateTimeShortMonth,
            this.$isMilitaryTimeEnabled(this.selectedWarehouse),
            this.novaCore.LuxonDateTimeFormats.LongDateTime24ShortMonth,
            this.locale
          )} (${this.selectedWarehouse.timezone})`
        : '';
    },
    refNumberPlaceholder() {
      return this.$refNumSettings(this.selectedWarehouse).helperText || this.referenceNumberLabel;
    },
    areWarehouseNotificationsEnabled() {
      return !this.$getSettingValue(
        'muteAppointmentNotifications',
        this.selectedWarehouse?.settings
      );
    },
    docksFilteredBySelectedLoadType() {
      let docks = this.warehouseDocks;
      if (this.selectedLoadType?.id) {
        docks = docks.filter(dock => {
          return dock.loadTypeIds.includes(this.selectedLoadType.id);
        });
      }

      return docks;
    },
    refNumValidationRulesOld() {
      // REMOVE when deleting enable-multi-ref-input-ui feature flag
      const displayName = this.$refNumSettings(this.selectedWarehouse).displayName;
      return [
        ...(this.$refNumSettings(this.selectedWarehouse).isRequired
          ? this.$validator.rules.required(displayName)
          : []),
        ...(this.$refNumSettings(this.selectedWarehouse).inputMode === 'numeric'
          ? this.$validator.rules.positiveInteger(this.$t('global.phrases.mustBeAValidNumber'))
          : [])
      ];
    },
    stepsTotal() {
      return this.shouldShowHandlingUnits ? 4 : 3;
    }
  },
  data() {
    return {
      loading: false,
      step: 1,
      submitted: false,
      selectedDocks: [],
      selectedLoadType: {},
      selectedCarrier: null,
      selectedDate: '',
      selectedTime: null,
      loadTypeTriggers: [],
      customFields: [],
      ccEmails: [],
      disableConfirm: false,
      mounted: false,
      notes: '',
      tags: [],
      isContextSet: false,
      customFormsData: [],
      fetchingOrgCarrierSettings: false,
      notificationsEnabled: true,
      assetDetailsPanel: false,
      warehouseDocks: [],
      unavailableDates: [],
      fetchingUnavailableDates: false
    };
  },
  methods: {
    /**
     * Submit the form
     * @public
     * @returns {Promise<void>}
     */
    async submit() {
      const isStep1Valid = this.validateStep1();
      const isStep2Valid = !this.shouldShowHandlingUnits || this.validateStep2();
      const isDateTimeValid = this.validateDateTimeStep();

      if (
        (await this.$isFormValid(this.$refs.form)) &&
        this.$refs.emailFieldList.validate() &&
        isStep1Valid &&
        isStep2Valid &&
        isDateTimeValid
      ) {
        return this.createAppointment();
      } else {
        this.$nextTick(() => {
          scrollFirstErrorIntoView();
        });
      }
    },
    /**
     * Create appointment
     * @public
     * @returns {Promise<void>}
     */
    async createAppointment() {
      this.loading = true;

      const appointmentData = this.buildAppointmentData();
      const docksToTry = this.getDocksToTry();

      if (!docksToTry.length) {
        this.notify('No docks have been selected', 'error');
        return;
      }

      let tryCount = 0;
      let isSuccessful = false;
      appointmentData.dockId = docksToTry[tryCount];

      try {
        // This should be in the backend by allowing API user to pass docks they want to attempt to book against
        // TODO: Investigate moving this to the backend.
        do {
          try {
            let response = await this.attemptCreateAppointment(appointmentData);
            if (response?.data) {
              isSuccessful = true;
              this.handleSuccessfulAppointment(response.data);
            }
          } catch (err) {
            const { errorMessage, errorCode } = this.handleAppointmentError(
              err,
              ++tryCount,
              docksToTry
            );
            if (errorMessage) {
              throw {
                message: errorMessage,
                errorCode
              };
            }
            appointmentData.dockId = docksToTry[tryCount];
          }
        } while (tryCount < docksToTry.length && !isSuccessful);
      } catch (err) {
        if (!isAxiosError(err)) {
          this.notify(err.message ?? 'An error occurred while creating the appointment', 'error', {
            title: notificationsHelper.getNotificationTitleByRespCode(err.errorCode)
          });
        }
      } finally {
        this.loading = false;
      }
    },

    buildAppointmentData() {
      const isClone = Boolean(this.context.id);
      const metadata = {
        ...(this.context.metadata ?? {}),
        clonedFromId: this.context.id
      };

      return {
        userId: this.selectedCarrier.id,
        warehouseId: this.selectedWarehouse.id,
        loadTypeId: this.selectedLoadType.id,
        start: this.selectedTime.start.toUTC().toISO(),
        refNumbers: this.computedRefNumbers,
        notes: this.notes,
        tags: this.tags,
        ccEmails: this.ccEmails,
        customFields: this.customFields,
        units: this.units,
        isClone,
        metadata: isClone ? metadata : null,
        muteNotifications: !this.areWarehouseNotificationsEnabled
          ? false
          : !this.notificationsEnabled,
        customFormsData: this.customFormsData
      };
    },

    getDocksToTry() {
      return this.selectedTime.docks || this.selectedDocks?.map(dock => dock.id) || [];
    },

    async attemptCreateAppointment(appointmentData) {
      return await this.$store.dispatch('Appointments/createAppointment', {
        ...appointmentData,
        requestConfig: { suppressNotification: true }
      });
    },

    handleAppointmentError(err, tryCount, docksToTry) {
      let errorMessage = err.response?.data?.message ?? err.message ?? '';
      const errorCode = err.response?.data?.errorCode ?? err.errorCode ?? null;
      const isPreExistingApptError = errorMessage.includes('preexisting appointment');

      if (isPreExistingApptError && tryCount === docksToTry.length) {
        errorMessage = 'Appointment conflicts with preexisting appointment';
      } else if (isPreExistingApptError) {
        errorMessage = null;
      }

      return {
        errorMessage,
        errorCode
      };
    },

    handleSuccessfulAppointment(data) {
      const entryPoint = this.mixpanel.getEntryPoint(this, [
        { entryPoint: 'Sidebar', component: 'navigation-drawer' },
        { entryPoint: 'Clone Appointment', component: 'appointment-details-dialog' },
        { entryPoint: 'Appointments Grid', component: 'appointments-page' },
        { entryPoint: 'Docks Page', component: 'dock-list' },
        { entryPoint: 'Warehouses Page', component: 'warehouses-page' },
        { entryPoint: 'Carrier Contacts Page', component: 'carrier-list' }
      ]);

      this.mixpanel.track(this.mixpanel.events.MODULE.APPOINTMENT.CREATED, {
        'Appointment ID': data.id,
        'Appointment Start': data.start,
        'Appointment Ref #': data.refNumbers.join(', '),
        'Appointment Tags': data.tags,
        'Handling Units': data.units ?? 'Not provided',
        'Entry Point': entryPoint,
        'Created By': 'Warehouse user',
        'Creation Status': data.status,
        'Warehouse ID': this.selectedWarehouse?.id,
        'Warehouse Name': this.selectedWarehouse?.name,
        'Org ID': this.$org.id,
        'Org Name': this.$org.name
      });

      notificationsHelper.showAppointmentSuccessNotification({
        appointmentData: data,
        notifyFn: this.notify,
        defaultMessage: 'Appointment has been created'
      });

      this.$emit('close');
      this.$emit('scheduled', data);
      if (this.followToAppointmentDetails) {
        this.handleAppointmentQueryString(this.$route.path, data.id);
      }
    },
    /**
     * Emit close event when form lives in dialog
     * @public
     */
    cancel() {
      /**
       * Emits close event
       * @public
       * @event close
       */
      this.$emit('close');
    },
    /**
     * proceed to next step
     * @public
     */
    async proceed() {
      if (this.step === 1) {
        await this.updateSelectedWarehouse();
      }

      // If moving from handling units step, fetch unavailable dates
      if (this.step === 2 && this.shouldShowHandlingUnits) {
        await this.fetchUnavailableDates();
      }

      if (this.step < this.stepsTotal) {
        this.step++;
        this.scrollToHeader();
      } else {
        await this.submit();
      }
    },
    /**
     * Go to previous step, handling dynamic step numbers
     * @public
     */
    goToPreviousStep() {
      if (this.step === this.dateTimeStep && !this.shouldShowHandlingUnits) {
        // If we're on dateTime step and handling units is hidden, skip step 2
        this.step = 1;
      } else if (this.step === this.appointmentDetailsStep && !this.shouldShowHandlingUnits) {
        // If we're on appointment details and handling units is hidden, go to dateTime
        this.step = this.dateTimeStep;
      } else {
        // Normal decrement
        this.step--;
      }
      this.scrollToHeader();
    },
    /**
     * Validate whether step 1 is completed or not
     * @public
     * @returns {boolean}
     */
    validateStep1() {
      return Boolean(
        this.selectedCarrier?.id &&
          this.selectedWarehouse?.id &&
          this.selectedLoadType?.id &&
          this.selectedDocks?.length > 0 &&
          this.selectedDocks?.every(Boolean)
      );
    },
    /**
     * Validate whether step 2 (handling units) is completed or not
     * @public
     * @returns {boolean}
     */
    validateStep2() {
      return this.validateUnitsStep(this.units);
    },
    /**
     * Validate whether date/time step is completed or not
     * @public
     * @returns {boolean}
     */
    validateDateTimeStep() {
      return Boolean(this.selectedTime?.start);
    },
    /**
     * Clears data dependent on warehouse when new warehouse is selected
     * @public
     */
    clearWarehouseContext() {
      this.selectedDocks = [];
      this.selectedLoadType = {};
    },
    toggleConfirmButton(disableButton) {
      this.disableConfirm = disableButton;
    },
    /**
     * if a value is passed and there is no context, the form will auto increment to the next step
     * @param value
     */
    autoIncrementStep(value) {
      const stepHasValue = Object.keys(value).length;
      const autoIncrementConditions = !Object.keys(this.context).length || this.assetVisit?.id;
      if (stepHasValue && autoIncrementConditions && this.mounted) {
        this.step++;
      }
    },
    scrollToHeader() {
      const titleElements = document.getElementsByClassName('v-stepper__header');
      if (titleElements?.length > 0) {
        titleElements[0].scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    },
    /**
     * Fetch unavailable dates based on handling units
     * @public
     * @param {Object} dateRange - Optional date range {start, end}
     * @returns {Promise<void>}
     */
    async fetchUnavailableDates(dateRange = null) {
      // Only fetch if handling units feature is enabled
      if (!this.shouldShowHandlingUnits) {
        this.unavailableDates = [];
        return;
      }

      if (!this.units || !this.selectedWarehouse?.id || !this.selectedDocks?.length) {
        this.unavailableDates = [];
        return;
      }

      this.fetchingUnavailableDates = true;
      try {
        // Use the shared fetch function from useLimiter
        const fetchFn = this.createFetchUnavailableDates(this.services, 5);
        this.unavailableDates = await fetchFn(
          {
            warehouseId: this.selectedWarehouse.id,
            dockId: this.selectedDocks[0].id,
            loadTypeId: this.selectedLoadType.id,
            units: this.units,
            timezone: this.selectedWarehouse.timezone
          },
          dateRange
        );
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
        this.unavailableDates = [];
      } finally {
        this.fetchingUnavailableDates = false;
      }
    },
    /**
     * Handle date range change from DateTimePicker
     * @public
     */
    async handleDateRangeChanged(dateRange) {
      await this.fetchUnavailableDates(dateRange);
    },
    async updateSelectedWarehouse() {
      if (this.selectedWarehouse?.id) {
        this.selectedWarehouse = await this.services.warehouse.getWarehouseById(
          this.selectedWarehouse.id,
          {},
          {
            fields: [
              ...this.services.warehouse.requestOptions.grid.fields,
              'customApptFieldsTemplate'
            ],
            joins: [
              'docks||name,loadTypeIds,isActive,capacityParentId,sortOrder',
              'docks.capacityChildren||name,isActive,capacityParentId,loadTypeIds,sortOrder',
              'org||name,settings'
            ]
          }
        );
      }
    },
    async buildContextFromAppointment(appointment) {
      const context = this.novaCore.deepClone(appointment);
      context.selectedWarehouse = context.dock?.warehouse;
      context.selectedDocks = [appointment.dock];
      context.selectedCarrier = appointment.user || {};
      context.selectedLoadType = appointment.loadType;

      return context;
    },
    setContext(context) {
      this.selectedWarehouse = context.selectedWarehouse || {};
      this.selectedCarrier = context.selectedCarrier || {};
      this.selectedDate =
        context.selectedDate ??
        DateTime.now({ zone: context.selectedWarehouse?.timezone ?? 'local' }).toISO();
      // When cloning the appointment, this property is never set
      // The user is forced to select a time to move forward.
      if (context.selectedTime?.start) {
        this.selectedTime = context.selectedTime;
      }
      this.customFields = context.customFields || [];
      const refNumbersToSet = context.refNumbers ?? (context.refNumber ? [context.refNumber] : []);
      this.refNumbers.splice(0, this.refNumbers.length, ...refNumbersToSet);
      this.refNumber = refNumbersToSet.join(',');
      this.notes = context.notes || '';
      this.tags = context.tags || [];
      this.ccEmails = context.ccEmails || [];
      this.units = context.units ?? null;
      this.selectedDocks = context.selectedDocks || [];
      this.selectedLoadType = context.selectedLoadType || {};
      this.notificationsEnabled = !context.muteNotifications;
      this.isContextSet = true;
    },
    async handleAvailabilityError() {
      /**
       * Back to step 1 after 750ms if there's a date/time availability error
       * The intention of the delay is just a UX improvement to avoid a fast transition between steps
       */
      setTimeout(() => {
        if (this.validateStep1() && this.step === this.dateTimeStep) {
          this.step = 1;
        }
      }, 750);
    }
  },
  async created() {
    // Check if the context is an actual appointment or pre-built context
    if (this.context?.id) {
      this.setContext(await this.buildContextFromAppointment(this.context));
    } else {
      this.setContext(this.context);
    }
  },
  mounted() {
    this.mounted = true;
  },
  watch: {
    selectedTime(value) {
      this.autoIncrementStep(value);
    },
    async selectedWarehouse(newWarehouse, oldWarehouse) {
      if (this.mounted) {
        const warehouseChanged =
          newWarehouse?.id && oldWarehouse?.id && newWarehouse.id !== oldWarehouse.id;
        this.notificationsEnabled = this.areWarehouseNotificationsEnabled;

        if (warehouseChanged) {
          this.clearWarehouseContext();
          this.customFields = newWarehouse.customFields ?? [];
        }
      }
      // If the newly selected warehouse does not have dock data, fetch docks so the dock select works correctly
      if (newWarehouse?.id && newWarehouse?.docks == null) {
        const warehouseDocks = await dockService.getDocksByWarehouseId(this.selectedWarehouse.id);
        this.warehouseDocks = warehouseDocks ?? [];
      } else if (newWarehouse?.id) {
        this.warehouseDocks = newWarehouse.docks;
      }
    },
    selectedLoadType(newValue, oldValue) {
      if (newValue?.id && newValue.id !== oldValue?.id) {
        customFormsService
          .getLoadTypeTriggers(newValue.id, {
            isActive: true,
            app: { $in: [this.novaCore.AppsEnum.All, this.novaCore.AppsEnum.Warehouse] }
          })
          .then(triggers => {
            this.loadTypeTriggers = triggers;
          });
      }
    },
    async selectedCarrier(newCarrier, oldCarrier) {
      if (oldCarrier?.email) {
        this.ccEmails.splice(this.ccEmails.indexOf(oldCarrier.email), 1);
      }
      if (newCarrier?.id) {
        this.fetchingOrgCarrierSettings = true;
        try {
          const orgCarrierSettings = newCarrier.orgCarrierSettings?.[0] ?? [];
          this.ccEmails = orgCarrierSettings?.emailCCs ?? [];
        } finally {
          this.fetchingOrgCarrierSettings = false;
        }
      }

      // Keep origin ccEmails when cloning an appointment
      if (this.context.id && this.context.ccEmails?.length > 0) {
        this.ccEmails = [...this.ccEmails, ...this.context.ccEmails].sort();
      }

      // Ensure there is no duplicates or rubbish
      this.ccEmails = compact(uniq([...this.ccEmails]));
    }
  },
  setup() {
    const dialogTeleportTarget = inject('dialogActionsRef');
    const { t, locale } = useI18n({ useScope: 'global' });
    const notify = useNotify();
    const validator = useValidator();
    const store = useStore();

    const selectedWarehouse = ref({});
    const { normalizeRefNumbers } = useAppointmentHelpers();

    const isMultiRefNumUIEnabledRef = useFlag('enable-multi-ref-input-ui');
    const isMultiRefNumUIEnabled = computed(
      () => isMultiRefNumUIEnabledRef?.value?.enabled === true
    );

    const refNumber = ref('');
    const refNumbers = ref([]);

    const computedRefNumbers = computed(() => {
      if (!isMultiRefNumUIEnabled.value) {
        return normalizeRefNumbers(null, refNumber.value);
      } else {
        return normalizeRefNumbers(refNumbers.value, null);
      }
    });

    watch(
      isMultiRefNumUIEnabled,
      isEnabled => {
        if (isEnabled) {
          refNumbers.value = normalizeRefNumbers(null, refNumber.value);
        } else {
          refNumber.value = normalizeRefNumbers(refNumbers.value, null).join(',');
        }
      },
      { immediate: true }
    );

    const refNumSettings = computed(() => {
      const getter = store.getters['Settings/refNumSettings'];
      return typeof getter === 'function' && selectedWarehouse.value
        ? getter(selectedWarehouse.value)
        : null;
    });

    const { multiRefNumValidationRules, refNumValidationRules } = useFieldRules(refNumSettings, {
      required: validator.rules.required,
      positiveInteger: validator.rules.positiveInteger,
      maxItems: validator.rules.selectLimit
    });

    const warehouseSettings = computed(() => selectedWarehouse.value?.settings);

    const units = ref(null);

    const {
      shouldShowHandlingUnits,
      handlingUnitsLabel,
      handlingUnitsHelperText,
      isHandlingUnitsRequired,
      handlingUnitsMin,
      handlingUnitsMax,
      handlingUnitsMinAttr,
      handlingUnitsMaxAttr,
      handlingUnitsRules,
      validateUnitsStep,
      getUnitsValidationRules,
      createFetchUnavailableDates
    } = useLimiter(warehouseSettings, useFlag, validator);

    return {
      dialogTeleportTarget,
      t,
      locale,
      notify,
      selectedWarehouse,
      multiRefNumValidationRules,
      refNumValidationRules,
      isMultiRefNumUIEnabled,
      refNumbers,
      refNumber,
      computedRefNumbers,
      refNumSettings,
      units,
      shouldShowHandlingUnits,
      handlingUnitsLabel,
      handlingUnitsHelperText,
      isHandlingUnitsRequired,
      handlingUnitsMin,
      handlingUnitsMax,
      handlingUnitsMinAttr,
      handlingUnitsMaxAttr,
      handlingUnitsRules,
      validateUnitsStep,
      getUnitsValidationRules,
      createFetchUnavailableDates
    };
  }
};
</script>

<style scoped lang="scss">
.sidebar-content {
  &.has-asset-visit {
    max-height: 75dvh;
    overflow: auto;
    padding-bottom: 65px;
  }
}

.step-label-truncate {
  max-width: 150px;
}
</style>
